import React, { useState } from 'react';
import { Plus, Trash, Pencil, VideoCamera, Link as LinkIcon } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';

const getStatusClassName = (status) => {
  if (status === 'live') return 'bg-red-500/20 text-red-500';
  if (status === 'completed') return 'bg-green-500/20 text-green-500';
  if (status === 'cancelled') return 'bg-zinc-500/20 text-zinc-500';
  return 'bg-blue-500/20 text-blue-500';
};

const getTierClassName = (tier) => {
  if (tier === 'elite') return 'bg-[#D4AF37]/20 text-[#D4AF37]';
  if (tier === 'premium') return 'bg-white/10 text-white';
  return 'bg-zinc-800 text-zinc-400';
};

const LiveSessionsTab = ({ sessions, courses, onCreateSession, onUpdateSession, onDeleteSession }) => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [sessionForm, setSessionForm] = useState({ 
    course_id: '', 
    title: '', 
    description: '', 
    scheduled_date: '',
    zoom_link: '',
    zoom_meeting_id: '',
    zoom_passcode: '',
    duration_minutes: 60,
    tier_required: 'basic'
  });

  const resetForm = () => {
    setSessionForm({ 
      course_id: '', 
      title: '', 
      description: '', 
      scheduled_date: '',
      zoom_link: '',
      zoom_meeting_id: '',
      zoom_passcode: '',
      duration_minutes: 60,
      tier_required: 'basic'
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    await onCreateSession({
      ...sessionForm,
      scheduled_date: new Date(sessionForm.scheduled_date).toISOString(),
      duration_minutes: parseInt(sessionForm.duration_minutes)
    });
    setIsCreateDialogOpen(false);
    resetForm();
  };

  const handleEdit = (session) => {
    setEditingSession(session);
    setSessionForm({
      course_id: session.course_id,
      title: session.title,
      description: session.description || '',
      scheduled_date: session.scheduled_date ? new Date(session.scheduled_date).toISOString().slice(0, 16) : '',
      zoom_link: session.zoom_link || '',
      zoom_meeting_id: session.zoom_meeting_id || '',
      zoom_passcode: session.zoom_passcode || '',
      duration_minutes: session.duration_minutes || 60,
      tier_required: session.tier_required || 'basic'
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    await onUpdateSession(editingSession.session_id, {
      ...sessionForm,
      scheduled_date: sessionForm.scheduled_date ? new Date(sessionForm.scheduled_date).toISOString() : undefined,
      duration_minutes: parseInt(sessionForm.duration_minutes)
    });
    setIsEditDialogOpen(false);
    setEditingSession(null);
    resetForm();
  };

  const handleStatusChange = async (sessionId, newStatus) => {
    await onUpdateSession(sessionId, { status: newStatus });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not scheduled';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const SessionFormFields = ({ onSubmit, submitLabel }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <Label className="text-zinc-400">Course</Label>
        <Select 
          value={sessionForm.course_id} 
          onValueChange={(value) => setSessionForm({ ...sessionForm, course_id: value })}
        >
          <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white">
            <SelectValue placeholder="Select a course" />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-zinc-700">
            {courses.map((course) => (
              <SelectItem key={course.course_id} value={course.course_id}>
                {course.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-zinc-400">Session Title</Label>
        <Input
          value={sessionForm.title}
          onChange={(e) => setSessionForm({ ...sessionForm, title: e.target.value })}
          className="bg-zinc-900 border-zinc-700 text-white"
          placeholder="e.g., Weekly Market Analysis"
          required
        />
      </div>
      <div>
        <Label className="text-zinc-400">Description</Label>
        <Textarea
          value={sessionForm.description}
          onChange={(e) => setSessionForm({ ...sessionForm, description: e.target.value })}
          className="bg-zinc-900 border-zinc-700 text-white"
          placeholder="What will be covered in this session?"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-400">Scheduled Date & Time</Label>
          <Input
            type="datetime-local"
            value={sessionForm.scheduled_date}
            onChange={(e) => setSessionForm({ ...sessionForm, scheduled_date: e.target.value })}
            className="bg-zinc-900 border-zinc-700 text-white"
            required
          />
          <p className="text-xs text-zinc-500 mt-1">Mondays at 5pm EST recommended</p>
        </div>
        <div>
          <Label className="text-zinc-400">Duration (minutes)</Label>
          <Input
            type="number"
            value={sessionForm.duration_minutes}
            onChange={(e) => setSessionForm({ ...sessionForm, duration_minutes: e.target.value })}
            className="bg-zinc-900 border-zinc-700 text-white"
          />
        </div>
      </div>
      <div className="border-t border-zinc-800 pt-4">
        <p className="text-sm text-zinc-400 mb-3 flex items-center gap-2">
          <VideoCamera className="w-4 h-4" /> Zoom Meeting Details
        </p>
        <div className="space-y-3">
          <div>
            <Label className="text-zinc-400">Zoom Link</Label>
            <Input
              value={sessionForm.zoom_link}
              onChange={(e) => setSessionForm({ ...sessionForm, zoom_link: e.target.value })}
              className="bg-zinc-900 border-zinc-700 text-white"
              placeholder="https://zoom.us/j/..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400">Meeting ID</Label>
              <Input
                value={sessionForm.zoom_meeting_id}
                onChange={(e) => setSessionForm({ ...sessionForm, zoom_meeting_id: e.target.value })}
                className="bg-zinc-900 border-zinc-700 text-white"
                placeholder="123 456 7890"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Passcode</Label>
              <Input
                value={sessionForm.zoom_passcode}
                onChange={(e) => setSessionForm({ ...sessionForm, zoom_passcode: e.target.value })}
                className="bg-zinc-900 border-zinc-700 text-white"
                placeholder="abc123"
              />
            </div>
          </div>
        </div>
      </div>
      <div>
        <Label className="text-zinc-400">Required Tier</Label>
        <Select 
          value={sessionForm.tier_required} 
          onValueChange={(value) => setSessionForm({ ...sessionForm, tier_required: value })}
        >
          <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-zinc-700">
            <SelectItem value="basic">Basic</SelectItem>
            <SelectItem value="premium">Premium</SelectItem>
            <SelectItem value="elite">Elite</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button type="submit" className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C158]">
        {submitLabel}
      </Button>
    </form>
  );

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl text-white font-outfit">Live Sessions</h2>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
              <Plus className="w-4 h-4 mr-2" />
              Schedule Session
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Schedule Live Session</DialogTitle>
            </DialogHeader>
            <SessionFormFields onSubmit={handleCreate} submitLabel="Schedule Session" />
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Live Session</DialogTitle>
          </DialogHeader>
          <SessionFormFields onSubmit={handleUpdate} submitLabel="Update Session" />
        </DialogContent>
      </Dialog>

      <div className="bg-[#121212] border border-white/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-black/30">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-mono uppercase text-zinc-500">Session</th>
                <th className="text-left px-4 py-3 text-xs font-mono uppercase text-zinc-500">Course</th>
                <th className="text-left px-4 py-3 text-xs font-mono uppercase text-zinc-500">Scheduled</th>
                <th className="text-left px-4 py-3 text-xs font-mono uppercase text-zinc-500">Zoom</th>
                <th className="text-left px-4 py-3 text-xs font-mono uppercase text-zinc-500">Status</th>
                <th className="text-left px-4 py-3 text-xs font-mono uppercase text-zinc-500">Tier</th>
                <th className="text-left px-4 py-3 text-xs font-mono uppercase text-zinc-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => (
                <tr key={session.session_id} className="border-t border-white/5">
                  <td className="px-4 py-3">
                    <p className="text-white font-medium">{session.title}</p>
                    <p className="text-xs text-zinc-500">{session.duration_minutes} min</p>
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-sm">{session.course_title}</td>
                  <td className="px-4 py-3 text-zinc-400 text-sm">{formatDate(session.scheduled_date)}</td>
                  <td className="px-4 py-3">
                    {session.zoom_link ? (
                      <span className="text-green-500 text-xs flex items-center gap-1">
                        <LinkIcon className="w-3 h-3" /> Added
                      </span>
                    ) : (
                      <span className="text-zinc-500 text-xs">Not set</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Select 
                      value={session.status} 
                      onValueChange={(value) => handleStatusChange(session.session_id, value)}
                    >
                      <SelectTrigger className={`w-28 h-7 text-xs ${getStatusClassName(session.status)}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-zinc-700">
                        <SelectItem value="scheduled">Scheduled</SelectItem>
                        <SelectItem value="live">Live</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 ${getTierClassName(session.tier_required)}`}>
                      {session.tier_required}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(session)}
                        className="text-zinc-400 hover:text-white hover:bg-zinc-800"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDeleteSession(session.session_id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default LiveSessionsTab;
